import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";
import i18n, { SUPPORTED_LANGS } from "./i18n";
import { resolveWorkspaceSelection } from "./workspaceSelection";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const EMPTY_PROFILE = {
  role: "tenant",
  tenant_id: null,
  active_tenant_id: null,
  tenant: null,
  is_superadmin: false,
  // Identidad de la PERSONA, separada de la del workspace. Hasta que existió `/app/perfil` no se
  // leía en ningún lado, y por eso el menú de usuario terminó mostrando el nombre del tenant.
  full_name: null,
  email: null,
};

export default function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);

  const loadTenantsAndProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null);
      setTenants([]);
      return [];
    }

    setWorkspaceLoading(true);
    try {
      // La RPC devuelve NULL cuando existe una invitación pendiente. Ese estado es válido:
      // evita crear un workspace personal antes de aceptar la invitación.
      const { error: initError } = await supabase.rpc("init_tenant_if_empty");
      if (initError) console.warn("[AuthProvider] init_tenant_if_empty", initError.message);

      const [membershipsResult, profileResult] = await Promise.all([
        supabase
          .from("tenant_members")
          // El embed va DESAMBIGUADO con el nombre de la FK a propósito: cualquier tabla nueva con FK a
        // `tenant_members` y a `tenants` a la vez haría que PostgREST vea un muchos-a-muchos, y este
        // select —del que depende entrar a la aplicación— fallaría con PGRST201.
        .select("role,tenant_id,joined_at,tenants!tenant_members_tenant_id_fkey(name,slug)")
          .eq("user_id", userId),
        supabase
          .from("profiles")
          .select("is_superadmin,locale,active_tenant_id,full_name,email")
          .eq("id", userId)
          .maybeSingle(),
      ]);

      if (membershipsResult.error) throw membershipsResult.error;

      let persistedProfile = profileResult.data;
      if (profileResult.error) {
        // Compatibilidad defensiva para una base anterior a Equipo Fase 1.
        const fallback = await supabase
          .from("profiles")
          .select("is_superadmin,locale,full_name,email")
          .eq("id", userId)
          .maybeSingle();
        persistedProfile = fallback.data;
      }

      if (persistedProfile?.locale && SUPPORTED_LANGS.includes(persistedProfile.locale)
        && i18n.resolvedLanguage !== persistedProfile.locale) {
        await i18n.changeLanguage(persistedProfile.locale);
      }

      // La identidad de la persona no depende de tener membresías: quien todavía no aceptó una
      // invitación también tiene nombre y correo.
      const identidad = {
        full_name: persistedProfile?.full_name || null,
        email: persistedProfile?.email || null,
      };

      const rows = membershipsResult.data || [];
      setTenants(rows);
      if (!rows.length) {
        setProfile({ ...EMPTY_PROFILE, ...identidad, is_superadmin: !!persistedProfile?.is_superadmin });
        return rows;
      }

      const { chosen, persistedActiveTenantId } = resolveWorkspaceSelection(
        rows,
        persistedProfile?.active_tenant_id,
      );
      setProfile({
        ...identidad,
        role: chosen.role,
        tenant_id: chosen.tenant_id,
        // Puede ser NULL aunque `tenant_id` tenga un fallback visual. Mantener ambos valores
        // separados evita confundir "lo que se muestra" con "lo que el backend tiene activo".
        active_tenant_id: persistedActiveTenantId,
        tenant: chosen.tenants || null,
        is_superadmin: !!persistedProfile?.is_superadmin,
      });
      return rows;
    } catch (error) {
      console.error("[AuthProvider] no se pudo cargar el workspace", error);
      setProfile(EMPTY_PROFILE);
      setTenants([]);
      return [];
    } finally {
      setWorkspaceLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (mounted) setSession(data.session ?? null);
      } catch (error) {
        console.error("[AuthProvider] getSession", error);
        if (mounted) setSession(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession ?? null);
      setLoading(false);
    });
    return () => { mounted = false; subscription.unsubscribe(); };
  }, []);

  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) {
      setProfile(null);
      setTenants([]);
      return;
    }
    loadTenantsAndProfile(userId);
  }, [session?.user?.id, loadTenantsAndProfile]);

  const refreshMemberships = useCallback(async () => {
    if (!session?.user?.id) return [];
    return loadTenantsAndProfile(session.user.id);
  }, [session?.user?.id, loadTenantsAndProfile]);

  const setActiveTenant = useCallback(async (tenantId) => {
    const membership = tenants.find((item) => item.tenant_id === tenantId);
    if (!membership) return { ok: false, error: new Error("Workspace no disponible") };
    // Sólo es no-op si el workspace está realmente persistido. Si la interfaz muestra el primer
    // membership como fallback pero `profiles.active_tenant_id` está NULL, hay que ejecutar la RPC.
    if (profile?.active_tenant_id === tenantId) return { ok: true, changed: false };

    setWorkspaceLoading(true);
    const { data, error } = await supabase.rpc("set_active_tenant", { p_tenant_id: tenantId });
    if (error) {
      setWorkspaceLoading(false);
      return { ok: false, error };
    }
    setProfile((current) => ({
      ...current,
      role: membership.role,
      tenant_id: membership.tenant_id,
      active_tenant_id: membership.tenant_id,
      tenant: membership.tenants || null,
    }));
    setWorkspaceLoading(false);
    return { ok: true, changed: !!data?.changed };
  }, [profile?.active_tenant_id, tenants]);

  const login = async (email, password) => {
    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) return { ok: false, error };
    setSession(data.session ?? null);
    return { ok: true, session: data.session ?? null };
  };

  const logout = async () => {
    setLoading(true);
    try { await supabase.auth.signOut(); }
    finally {
      setSession(null);
      setProfile(null);
      setTenants([]);
      setLoading(false);
    }
  };

  const setLocale = async (lng) => {
    if (!SUPPORTED_LANGS.includes(lng)) return;
    await i18n.changeLanguage(lng);
    if (session?.user?.id) {
      const { error } = await supabase.from("profiles").update({ locale: lng }).eq("id", session.user.id);
      if (error) console.warn("[AuthProvider] no se pudo guardar locale", error.message);
    }
  };

  // Nombre para mostrar de la persona. La columna `full_name` es una de las dos únicas que
  // `authenticated` puede escribir en `profiles` (la otra es `locale`): el GRANT es por columna, así
  // que esto no puede tocar `is_superadmin` ni `active_tenant_id` aunque alguien lo intente.
  const updateDisplayName = useCallback(async (nombre) => {
    const userId = session?.user?.id;
    const limpio = (nombre || "").trim();
    if (!userId) return { ok: false, error: new Error("sin_sesion") };
    if (!limpio) return { ok: false, error: new Error("nombre_vacio") };

    const { error } = await supabase.from("profiles").update({ full_name: limpio }).eq("id", userId);
    if (error) return { ok: false, error };

    setProfile((current) => ({ ...(current || EMPTY_PROFILE), full_name: limpio }));
    return { ok: true };
  }, [session?.user?.id]);

  // Nombre visible del WORKSPACE (distinto de `updateDisplayName`, que es la persona). Va por RPC
  // y no por UPDATE directo: `tenants` sólo tiene policy y GRANT de SELECT para `authenticated`, así
  // que el nombre que se tipeó una vez en el alta no tenía forma de corregirse. Ver la migración
  // `20260816000000_workspace_name_rpc`.
  const updateWorkspaceName = useCallback(async (nombre) => {
    const tenantId = profile?.tenant_id;
    const limpio = (nombre || "").trim();
    if (!tenantId) return { ok: false, error: new Error("sin_workspace") };
    if (limpio.length < 2) return { ok: false, error: new Error("nombre_corto") };
    if (limpio.length > 60) return { ok: false, error: new Error("nombre_largo") };

    const { data, error } = await supabase.rpc("update_workspace_name", {
      p_tenant_id: tenantId,
      p_name: limpio,
    });
    if (error) return { ok: false, error };

    // Se usa lo que devolvió la base, no lo que se tipeó: la RPC normaliza y es la fuente de verdad.
    const guardado = data || limpio;
    setProfile((current) => ({
      ...(current || EMPTY_PROFILE),
      tenant: { ...(current?.tenant || {}), name: guardado },
    }));
    // El selector de workspace del sidebar se pinta desde `tenants`, no desde `profile`: sin esto el
    // nombre viejo sigue ahí hasta recargar la página.
    setTenants((rows) => rows.map((row) => (
      row.tenant_id === tenantId ? { ...row, tenants: { ...(row.tenants || {}), name: guardado } } : row
    )));
    return { ok: true, name: guardado };
  }, [profile?.tenant_id]);

  const tenant = useMemo(() => profile?.tenant_id ? {
    id: profile.tenant_id,
    name: profile.tenant?.name || null,
    slug: profile.tenant?.slug || null,
  } : null, [profile]);

  const value = {
    session,
    profile,
    tenant,
    tenants,
    loading,
    workspaceLoading,
    login,
    logout,
    setLocale,
    updateDisplayName,
    updateWorkspaceName,
    setActiveTenant,
    refreshMemberships,
    supabase,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
